import { ChevronDown, ChevronRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui";
import React from "react";
import { Loader } from "../Loader";
import { cn } from "@/lib/utils";
import { RecursiveSubRowsProps } from "./types";

const RecursiveSubRows = ({ 
  row,
  nestedColumns = [], // Array of column definitions for each nested level
  levelIndex = 0,     // Current nested depth index (0 = level 2, 1 = level 3, etc.)
  expandedCellClassName,
  renderExpandedContent,
  icons,
  hideHeader,
  isSecondary,
  separateExpandColumn
}: RecursiveSubRowsProps) => {
  // Dynamically select columns for the current nesting depth
  const currentColumns = nestedColumns[levelIndex] || [];
  
  // Dynamic header visibility check based on current nesting level
  const levelKey = `level${levelIndex + 2}` as keyof typeof hideHeader; // Maps 0 -> level2, 1 -> level3, etc.
  const isHeaderHidden = !!hideHeader?.[levelKey];

  return (
    <TableRow className={cn(isSecondary ? "" : levelIndex % 2 == 0 ? "bg-blue-50 hover:bg-blue-50" : "bg-white hover:bg-white")}>
      <TableCell
        colSpan={row.getVisibleCells().length + 1}
        className={expandedCellClassName}
      >
        {renderExpandedContent ? (
          renderExpandedContent(row, levelIndex)
        ) : (
          <div className="p-4">
            <Table className={cn(isSecondary ? "border-1" : "")}>
              {!isHeaderHidden && (
                <TableHeader className={cn(isSecondary ? "bg-blue-50 hover:bg-blue-50" : "")}>
                  <TableRow>
                    {separateExpandColumn && (
                      <TableHead className="text-left h-[50px] text-primary w-[44px] bg-inherit" />
                    )}
                    {currentColumns.map((col: any) => {
                      if (col?.canHide?.(row?.original)) return;
                      return (
                        <TableHead
                          key={col.accessorKey || col.header}
                          className={cn(
                            col?.meta?.align === "right" ? "text-right" : "text-left",
                            "text-black font-[600]"
                          )}
                        >
                          {col.header}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                </TableHeader>
              )}
              
              <TableBody>
                {(row?.original as any)?.children ? (
                  (row?.original as any)?.children?.map(
                    (child: any, idx: number) => {
                      const childRowId = `${row.id}_child_${idx}`;
                      const hasNestedChildren = !!(child?.children?.length && Array.isArray(child.children));
                      
                      // Create a pseudo-row model state matching TanStack's structural properties
                      const pseudoRow = {
                        id: childRowId,
                        original: child,
                        tableMeta: row.tableMeta, // Preserve tracking metadata for recursive children
                        getVisibleCells: () => row.getVisibleCells(),
                      };

                      return (
                        <React.Fragment key={childRowId}>
                          <TableRow className={cn(isSecondary ? row.tableMeta?.isChildExpanded?.(childRowId) ? "border-0" : "border-1" : idx % 2 == 0 ? "bg-blue-50 hover:bg-blue-50" : "")}>
                            {separateExpandColumn ? hasNestedChildren ? (
                              <TableCell className="h-[45px] w-[44px]">
                                <button
                                  type="button"
                                  onClick={() => row.tableMeta?.toggleChildExpanded?.(childRowId)}
                                  className="flex items-center gap-2 border border-primary rounded-full p-0.5"
                                >
                                  {row.tableMeta?.isChildExpanded?.(childRowId) ? (
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </TableCell>
                            ): <div></div> : <></>}

                            {currentColumns.map((col: any, colIdx: number) => {
                              if (col?.canHide?.(row?.original)) return;
                              return (
                                <TableCell
                                  key={col.accessorKey || colIdx}
                                  className={cn(
                                    col?.meta?.align === "right" ? "text-right" : "text-left",
                                    "text-g-500 font-[400]"
                                  )}
                                >
                                  <div
                                    className={cn(
                                      "flex items-center gap-2",
                                      col?.meta?.align === "right" ? "justify-end" : ""
                                    )}
                                    style={{ width: col.size ? col.size : "auto" }}
                                  >
                                    {/* Render expand button inside sub-rows if nested children exist */}
                                    {colIdx === 0 && hasNestedChildren && !separateExpandColumn && (
                                      <button
                                        type="button"
                                        onClick={() => row.tableMeta?.toggleChildExpanded?.(childRowId)}
                                        className="flex items-center gap-2 border border-primary rounded-full p-0.5"
                                      >
                                        {row.tableMeta?.isChildExpanded?.(childRowId) ? (
                                          <ChevronDown className="w-3.5 h-3.5" />
                                        ) : (
                                          <ChevronRight className="w-3.5 h-3.5" />
                                        )}
                                      </button>
                                    )}

                                    {/* Render level icons dynamically */}
                                    {colIdx === 0 && (
                                      icons?.[`level${levelIndex + 2}` as keyof typeof icons] || null
                                    )}

                                    <span className="text-wrap">
                                      {col.accessorFn
                                        ? col.accessorFn(child)
                                        : col.cell
                                        ? col.cell(child)
                                        : child[col.accessorKey]}
                                    </span>
                                  </div>
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {/* Recurse deeper down the branch tree if active */}
                          {hasNestedChildren && row.tableMeta?.isChildExpanded?.(childRowId) && (
                            <RecursiveSubRows 
                              row={pseudoRow}
                              nestedColumns={nestedColumns}
                              levelIndex={levelIndex + 1} // Increment level index for deeper sub-rows
                              expandedCellClassName={expandedCellClassName}
                              renderExpandedContent={renderExpandedContent}
                              separateExpandColumn={separateExpandColumn}
                              icons={icons}
                              hideHeader={hideHeader}
                              isSecondary={isSecondary}
                            />
                          )}
                        </React.Fragment>
                      );
                    }
                  )
                ) : (
                  <Loader />
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default RecursiveSubRows;
